import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import {
  CodeBracketSquareIcon,
  BoltIcon,
  CheckCircleIcon,
  XCircleIcon,
  CommandLineIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

const codeSnippets = [
    {
      title: 'Variable Declaration',
      code: 'const nutzerName = "Fatli";',
      question: 'Cila është gjinia e fjalës "Name" në gjermanisht?',
      options: ['Der Name', 'Die Name', 'Das Name'],
      correct: 'Der Name',
      explanation: '"Name" është gjithmonë gjinia mashkullore (Der Name) në gjermanisht, prandaj përdoret "nutzerName" pa prapashtesa shtesë.'
    },
    {
      title: 'Boolean Logic',
      code: 'if (istEingeloggt === true) { ... }',
      question: 'Si thuhet "i lidhur" ose "i kyçur" në gjermanisht?',
      options: ['Eingeloggt', 'Verbunden', 'Gelöscht'],
      correct: 'Eingeloggt',
      explanation: '"Eingeloggt" vjen nga folja "einloggen", ndërsa "verbunden" përdoret më shumë për lidhje rrjeti.'
    },
    {
      title: 'Function Syntax',
      code: 'function ladeDaten() { ... }',
      question: 'Çfarë do të thotë folja "laden" në këtë kontekst?',
      options: ['Ngarkoj', 'Fshij', 'Printoj'],
      correct: 'Ngarkoj',
      explanation: '"Daten laden" do të thotë të ngarkosh të dhënat nga një burim i jashtëm si API ose DB.'
    },
    {
      title: 'Array Methods',
      code: 'const liste = ["Apfel", "Birne"]; \nliste.push("Orange");',
      question: 'Si thuhet "shto/shtyj" në gjermanisht për listat?',
      options: ['Schiessen', 'Drücken', 'Hinzufügen'],
      correct: 'Hinzufügen',
      explanation: '"Hinzufügen" është fjala standarde për të shtuar diçka, ndërsa "push" në kod bën pikërisht këtë.'
    },
    {
      title: 'Error Handling',
      code: 'try { \n  sendData(); \n} catch (fehler) { ... }',
      question: 'Si përkthehet fjala "Fehler" në shqip?',
      options: ['Sukses', 'Gabim', 'Mesazh'],
      correct: 'Gabim',
      explanation: '"Der Fehler" është gabimi. Në programim është shumë e rëndësishme kapja e gabimeve (Error Catching).'
    },
    {
      title: 'String Interpolation',
      // eslint-disable-next-line no-template-curly-in-string -- displayed as sample JS code
      code: 'console.log(`Hallo ${nutzer}`);',
      question: 'Cila është përshëndetja zyrtare "Mirëdita" në gjermanisht?',
      options: ['Guten Tag', 'Hallo', 'Tschüss'],
      correct: 'Guten Tag',
      explanation: '"Guten Tag" është zyrtare, ndërsa "Hallo" përdoret më shumë me kolegët (informale).'
    },
    {
      title: 'Conditional Logic',
      code: 'if (alter >= 18) { darfWählen = true; }',
      question: 'Si thuhet "mosha" në gjermanisht?',
      options: ['Das Alter', 'Die Alt', 'Der Alter'],
      correct: 'Das Alter',
      explanation: '"Alter" (mosha) është e gjinisë asnjëanëse (Das Alter).'
    },
    {
      title: 'Array Length',
      code: 'if (benutzer.length === 0) { ... }',
      question: 'Cila është shumësia e fjalës "Benutzer" (Përdorues)?',
      options: ['Die Benutzern', 'Die Benutzer', 'Die Benutzere'],
      correct: 'Die Benutzer',
      explanation: 'Fjala "Benutzer" nuk ndryshon në shumës, vetëm artikulli bëhet "Die".'
    },
    {
      title: 'Async/Await',
      code: 'async function holeDaten() { \n  const r = await fetch(url); \n}',
      question: 'Çfarë do të thotë folja "holen" në shqip?',
      options: ['Dërgoj', 'Marr/Sjell', 'Kërkoj'],
      correct: 'Marr/Sjell',
      explanation: '"Holen" përdoret kur marrim të dhënat (fetch data). "Hole Daten" = Merr të dhënat.'
    },
    {
      title: 'Data Types',
      code: 'let istAktiv = false;',
      question: 'Si thuhet "E saktë" dhe "E gabuar" (True/False)?',
      options: ['Richtig / Falsch', 'Ja / Nein', 'Gut / Schlecht'],
      correct: 'Richtig / Falsch',
      explanation: 'Në logjikën programuese "Wahr/Falsch" ose "Richtig/Falsch" korrespondojnë me True/False.'
    },
    {
      title: 'Object Properties',
      code: 'const benutzer = { \n  vorname: "Fatli", \n  nachname: "Hoxha" \n};',
      question: 'Si thuhet "mbiemri" në gjermanisht?',
      options: ['Der Nachname', 'Die Nachname', 'Das Nachname'],
      correct: 'Der Nachname',
      explanation: '"Nachname" (mbiemri) dhe "Vorname" (emri) janë të gjinisë mashkullore (Der Nachname/Der Vorname).'
    },
    {
      title: 'Loop Execution',
      code: 'while (verbindungIstAktiv === true) { \n  warteAufDaten(); \n}',
      question: 'Çfarë do të thotë folja "warten" në shqip?',
      options: ['Pres', 'Dërgoj', 'Përfundoj'],
      correct: 'Pres',
      explanation: '"Warten" do të thotë të presësh (to wait). Në programim, "warte" shpesh nënkupton pauzë ose pritje të një sinjali.'
    },
    {
      title: 'API Requests',
      code: 'function sendeAntwort() { \n  return response.send("Erfolgreich"); \n}',
      question: 'Si përkthehet fjala "Erfolgreich" në shqip?',
      options: ['Me sukses', 'Dështim', 'Në proces'],
      correct: 'Me sukses',
      explanation: '"Erfolgreich" do të thotë i suksesshëm. Në kontekstin e API-ve tregon që operacioni u krye me sukses.'
    },
    {
      title: 'User Authentication',
      code: 'if (!passwort.istGueltig()) { \n  console.error("Ungültiges Passwort!"); \n}',
      question: 'Çfarë do të thotë "Ungültig" në gjermanisht?',
      options: ['I pavlefshëm', 'I vlefshëm', 'Sekret'],
      correct: 'I pavlefshëm',
      explanation: '"Ungültig" do të thotë i pavlefshëm (invalid), ndërsa "Gültig" do të thotë i vlefshëm (valid).'
    },
    {
      title: 'File Operations',
      code: 'function loescheDatei(pfad) { \n  fs.unlinkSync(pfad); \n}',
      question: 'Si përkthehet folja "löschen" në shqip?',
      options: ['Fshij', 'Krijoj', 'Kopjoj'],
      correct: 'Fshij',
      explanation: '"Löschen" do të thotë fshij (delete). Në programim përdoret shumë për skedarët (Dateien löschen) ose të dhënat.'
    }
];

const ProgrammerGame = () => {
  const [shuffledSnippets, setShuffledSnippets] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const { user, updateUserStats } = useAuth();

  useEffect(() => {
    setShuffledSnippets([...codeSnippets].sort(() => Math.random() - 0.5));
  }, []);

  const handleSelect = (option) => {
    if (selectedOption !== null) return;

    setSelectedOption(option);
    const correct = option === shuffledSnippets[currentIdx].correct;
    if (correct) {
      setScore(score + 10);
    }
    setShowExplanation(true);
  };

  const finishGame = async () => {
    const totalXp = (user.xp || 0) + score;
    await updateUserStats({ xp: totalXp });
    setCompleted(true);
  };

  const handleNext = () => {
    if (currentIdx < shuffledSnippets.length - 1) {
      setCurrentIdx(currentIdx + 1);
      setSelectedOption(null);
      setShowExplanation(false);
    } else {
      finishGame();
    }
  };

  if (shuffledSnippets.length === 0) return null;

  if (completed) {
    return (
      <Card className="bg-zinc-950 text-white border-zinc-800 border border-white/10 p-10 rounded-[2.5rem] text-center shadow-2xl">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-green-400">
          <CheckCircleIcon className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2 italic">Deploy i Suksesshëm!</h2>
        <p className="text-brand-text/50 mb-8 font-medium text-sm">Zotëruat terminologjinë. XP Totale: {user.xp + score}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-10 py-3 bg-green-500 text-black font-black rounded-xl shadow-lg active:scale-95 transition-all text-sm"
        >
          Luaj Tjetër
        </button>
      </Card>
    );
  }

  const currentSnippet = shuffledSnippets[currentIdx];

  return (
    <Card className="bg-zinc-950 text-white border-zinc-800 overflow-hidden shadow-2xl border border-white/10 flex flex-col font-mono relative">
      <div className="bg-black/40 px-6 py-4 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-4">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-brand-primary/80"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
          </div>
          <span className="text-[10px] font-bold text-white/40 uppercase tracking-widest pl-2">German_Dev.sh</span>
        </div>
        <div className="flex items-center gap-2 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20">
          <BoltIcon className="w-3 h-3 text-green-400" />
          <span className="text-[10px] font-bold text-green-400">{score} XP</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 max-h-[500px]">
        {/* Code Editor Part */}
        <div className="p-8 bg-black/20 border-r border-white/5 overflow-hidden flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <CodeBracketSquareIcon className="w-4 h-4 text-yellow-400" />
            <h3 className="text-white font-bold text-sm italic">{currentSnippet.title}</h3>
          </div>

          <div className="relative group flex-1 min-h-0 overflow-hidden">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-yellow-500 to-green-500 rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <pre className="relative h-full bg-[#0d0d0d] p-6 rounded-xl text-green-400 text-xs overflow-x-auto border border-white/5 leading-relaxed">
              <code>{currentSnippet.code}</code>
            </pre>
          </div>

          <div className="mt-4 flex items-center gap-3 text-[9px] text-white/20 uppercase tracking-[0.2em] font-bold">
            <CommandLineIcon className="w-3 h-3" />
            Linguistics Debugger Active
          </div>
        </div>

        {/* Interaction Part */}
        <div className="p-8 flex flex-col justify-center bg-gradient-to-br from-gray-900 to-black overflow-y-auto">
          <h2 className="text-lg font-bold text-white mb-6 leading-relaxed">
            <span className="text-yellow-400 block mb-1 text-[10px] uppercase tracking-widest">Pyetja:</span>
            {currentSnippet.question}
          </h2>

          <div className="space-y-2">
            {currentSnippet.options.map((opt, i) => {
              const isThisCorrect = opt === currentSnippet.correct;
              const isThisSelected = selectedOption === opt;

              let btnClass = "bg-white/5 border-white/5 text-brand-text/50 hover:bg-white/10 hover:border-white/10";
              if (selectedOption !== null) {
                if (isThisCorrect) btnClass = "bg-green-500/20 border-green-500/50 text-green-400 shadow-lg shadow-green-500/10";
                else if (isThisSelected) btnClass = "bg-red-500/20 border-red-500/50 text-red-500 opacity-50";
                else btnClass = "bg-transparent border-transparent text-brand-text/80 pointer-events-none";
              }

              return (
                <button
                  key={i}
                  onClick={() => handleSelect(opt)}
                  disabled={selectedOption !== null}
                  className={`w-full p-4 text-left border-2 rounded-xl font-bold text-xs transition-all flex justify-between items-center group ${btnClass}`}
                >
                  <span>{opt}</span>
                  <div className={`w-4 h-4 rounded-md border flex items-center justify-center ${isThisSelected ? 'border-current' : 'border-white/10'}`}>
                    {selectedOption !== null && (isThisCorrect ? <CheckCircleIcon className="w-3 h-3" /> : isThisSelected && <XCircleIcon className="w-3 h-3" />)}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Explanation */}
          <div className={`mt-6 transition-all duration-500 ${showExplanation ? 'opacity-100' : 'opacity-0 pointer-events-none translate-y-4'}`}>
            <div className="bg-brand-primary/10 border border-yellow-500/20 p-4 rounded-xl mb-4 flex gap-3">
              <LightBulbIcon className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-[9px] font-black text-yellow-400 uppercase tracking-widest mb-0.5 italic">Pse kjo përgjigje?</h4>
                <p className="text-[11px] text-white/60 leading-relaxed">{currentSnippet.explanation}</p>
              </div>
            </div>

            <button
              onClick={handleNext}
              className="w-full py-3 bg-white text-black rounded-xl font-black text-xs hover:bg-brand-primary/10 transition-all flex items-center justify-center gap-2 active:scale-95 shadow-xl"
            >
              Vazhdo
              <CheckCircleIcon className="w-4 h-4 opacity-30" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ProgrammerGame;